import React, { useEffect, useId, useRef, useState } from 'react';
import { api } from '../../lib/api';

// The login page's "Request access" form. It renders INSIDE `.argus-login`, so it reuses that
// page's scoped tokens and field/button/banner classes (LoginPage.tsx) and adds only what a
// modal needs, under the same `al-` prefix. English-only, like the rest of the login page.
//
// Zero.Auth relays the details to the Argus team's inbox (POST /auth/access-request). Nothing is
// mailed to the requester — their address travels only as Reply-To. This address is duplicated
// from Zero.Auth's `AccessRequests:Inbox` default on purpose: it is the fallback shown when the
// relay itself fails, so it cannot come from the relay.
export const ACCESS_INBOX = 'access@argusports.com';

const MODAL_CSS = `
.argus-login .al-modal-backdrop{
  position:fixed; inset:0; z-index:50; display:flex; align-items:center; justify-content:center;
  padding:16px; background:rgba(3,4,8,0.72); backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px);
  animation:al-fade .16s ease-out;
}
.argus-login .al-modal{
  position:relative; width:100%; max-width:560px; max-height:calc(100vh - 32px); overflow-y:auto;
  background:#080A12; border:1px solid var(--hair2); border-radius:18px;
  box-shadow:0 30px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(120,124,255,0.06);
  padding:32px; animation:al-pop .18s ease-out;
}
@keyframes al-fade{ from{ opacity:0; } }
@keyframes al-pop{ from{ opacity:0; transform:translateY(8px) scale(.985); } }
.argus-login .al-modal-x{
  position:absolute; top:16px; right:16px; width:34px; height:34px; border-radius:10px;
  display:grid; place-items:center; cursor:pointer; background:none;
  border:1px solid transparent; color:var(--altext2); transition:.15s;
}
.argus-login .al-modal-x:hover{ border-color:var(--hair2); color:var(--altext); }
.argus-login .al-modal-x:disabled{ opacity:.4; cursor:not-allowed; }
.argus-login .al-modal-x svg{ width:16px; height:16px; }
.argus-login .al-modal-title{ font-family:var(--aldisp); font-weight:600; font-size:24px; letter-spacing:-0.02em; margin:10px 0 6px; color:var(--altext); }
.argus-login .al-modal-grid{ display:grid; grid-template-columns:1fr 1fr; gap:0 14px; }
.argus-login .al-modal-grid .al-span{ grid-column:1 / -1; }
.argus-login .al-opt{ font-weight:500; color:var(--altext3); }
.argus-login .al-inp.al-area{ height:auto; padding:12px 14px; }
.argus-login .al-inp textarea{
  flex:1; min-width:0; min-height:88px; resize:vertical; background:none; border:none; outline:none;
  color:var(--altext); font-family:var(--alui); font-size:14.5px; line-height:1.5;
}
.argus-login .al-inp textarea::placeholder{ color:var(--altext3); }
.argus-login .al-banner a{ color:inherit; font-weight:700; }
.argus-login .al-hp{ position:absolute; left:-10000px; top:auto; width:1px; height:1px; overflow:hidden; }
.argus-login .al-sent{ text-align:center; padding:12px 4px 4px; }
.argus-login .al-sent-mark{
  width:56px; height:56px; margin:0 auto 18px; border-radius:50%; display:grid; place-items:center;
  background:linear-gradient(120deg,var(--a),var(--b)); color:#fff;
  box-shadow:0 12px 30px -10px rgba(120,124,255,0.7);
}
.argus-login .al-sent-mark svg{ width:26px; height:26px; }
.argus-login .al-sent .al-lede{ margin:0 auto 26px; max-width:380px; }
@media (max-width:560px){
  .argus-login .al-modal{ padding:26px 20px 22px; }
  .argus-login .al-modal-grid{ grid-template-columns:1fr; }
}
@media (prefers-reduced-motion: reduce){
  .argus-login .al-modal-backdrop,.argus-login .al-modal{ animation:none; }
}
`;

// Same caps Zero.Auth enforces (AccessRequestService.Limits).
const MAX = { name: 120, email: 254, club: 120, role: 120, phone: 40, message: 2000 } as const;

type Fields = {
  name: string;
  email: string;
  club: string;
  role: string;
  phone: string;
  message: string;
  website: string; // honeypot — never shown to a person
};

const EMPTY: Fields = {
  name: '',
  email: '',
  club: '',
  role: '',
  phone: '',
  message: '',
  website: '',
};

type Failure = { message: string; offerInbox: boolean };

function failureFrom(err: unknown): Failure {
  const response = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
  // A 400 is something the visitor can fix in the form, so say what; anything else means the
  // request did not reach us, and the only useful answer is another way to reach us.
  if (response?.status === 400 && response.data?.error) {
    return { message: response.data.error, offerInbox: false };
  }
  if (response?.status === 429) {
    return { message: "You've sent a few requests already.", offerInbox: true };
  }
  return { message: "We couldn't send your request.", offerInbox: true };
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled])';

export function AccessRequestModal({ onClose }: { onClose: () => void }) {
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [status, setStatus] = useState<'form' | 'sending' | 'sent'>('form');
  const [failure, setFailure] = useState<Failure | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const titleId = useId();

  const sending = status === 'sending';
  // Read through a ref so the document listener below is bound once and still sees the latest.
  const sendingRef = useRef(sending);
  sendingRef.current = sending;

  const close = () => {
    if (!sendingRef.current) onClose();
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  // Focus in on open, back to whatever opened it on close; lock the page behind; Escape closes.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstFieldRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, []);

  // Keep Tab inside the dialog.
  const trapTab = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const set =
    (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFields((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setFailure(null);
    try {
      await api.post('/auth/access-request', fields);
      setStatus('sent');
    } catch (err) {
      setFailure(failureFrom(err));
      setStatus('form');
    }
  };

  const firstName = fields.name.trim().split(/\s+/)[0];

  return (
    <div
      className="al-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <style>{MODAL_CSS}</style>
      <div
        ref={dialogRef}
        className="al-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={trapTab}
      >
        <button
          type="button"
          className="al-modal-x"
          onClick={close}
          disabled={sending}
          aria-label="Close"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {status === 'sent' ? (
          <div className="al-sent">
            <div className="al-sent-mark">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h2 id={titleId} className="al-modal-title">
              Request sent
            </h2>
            <p className="al-lede">
              Thanks{firstName ? `, ${firstName}` : ''}. We have your details and will be in touch
              shortly to set up a conversation.
            </p>
            <button type="button" className="al-btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="al-kicker">Request access</div>
            <h2 id={titleId} className="al-modal-title">
              Let&rsquo;s talk
            </h2>
            <p className="al-lede">
              Leave your details and our team will get in touch to arrange a conversation.
            </p>

            {failure && (
              <div className="al-banner err" role="alert">
                <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  {failure.message}
                  {failure.offerInbox && (
                    <>
                      {' '}
                      Please email us at <a href={`mailto:${ACCESS_INBOX}`}>{ACCESS_INBOX}</a>.
                    </>
                  )}
                </span>
              </div>
            )}

            <div className="al-modal-grid">
              <div className="al-field">
                <label htmlFor="al-req-name">Full name</label>
                <div className="al-inp">
                  <input
                    id="al-req-name"
                    ref={firstFieldRef}
                    value={fields.name}
                    onChange={set('name')}
                    required
                    maxLength={MAX.name}
                    autoComplete="name"
                  />
                </div>
              </div>
              <div className="al-field">
                <label htmlFor="al-req-email">Work email</label>
                <div className="al-inp">
                  <input
                    id="al-req-email"
                    type="email"
                    value={fields.email}
                    onChange={set('email')}
                    required
                    maxLength={MAX.email}
                    autoComplete="email"
                    placeholder="you@club.com"
                  />
                </div>
              </div>
              <div className="al-field">
                <label htmlFor="al-req-club">Club / organisation</label>
                <div className="al-inp">
                  <input
                    id="al-req-club"
                    value={fields.club}
                    onChange={set('club')}
                    required
                    maxLength={MAX.club}
                    autoComplete="organization"
                  />
                </div>
              </div>
              <div className="al-field">
                <label htmlFor="al-req-role">Your role</label>
                <div className="al-inp">
                  <input
                    id="al-req-role"
                    value={fields.role}
                    onChange={set('role')}
                    required
                    maxLength={MAX.role}
                    autoComplete="organization-title"
                    placeholder="e.g. Head of Performance"
                  />
                </div>
              </div>
              <div className="al-field al-span">
                <label htmlFor="al-req-phone">
                  Phone <span className="al-opt">(optional)</span>
                </label>
                <div className="al-inp">
                  <input
                    id="al-req-phone"
                    type="tel"
                    value={fields.phone}
                    onChange={set('phone')}
                    maxLength={MAX.phone}
                    autoComplete="tel"
                  />
                </div>
              </div>
              <div className="al-field al-span">
                <label htmlFor="al-req-message">
                  Anything we should know? <span className="al-opt">(optional)</span>
                </label>
                <div className="al-inp al-area">
                  <textarea
                    id="al-req-message"
                    value={fields.message}
                    onChange={set('message')}
                    maxLength={MAX.message}
                    rows={3}
                    placeholder="What you'd like Argus to help with, and a good time to talk"
                  />
                </div>
              </div>
            </div>

            {/* Honeypot: off-screen rather than display:none, which some bots skip. */}
            <div className="al-hp" aria-hidden="true">
              <label htmlFor="al-req-website">Website</label>
              <input
                id="al-req-website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={fields.website}
                onChange={set('website')}
              />
            </div>

            <button type="submit" className="al-btn-primary" disabled={sending}>
              {sending ? (
                <>
                  <span className="al-spin" />
                  Sending…
                </>
              ) : (
                'Send request'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
